/**
 * The "Needs a Human" triage queue (#369).
 *
 * A message lands here when ingest could not attach it to an owner: `unmatched` when even
 * the association is unknown, `needs-lot` when the association resolved but the lot did
 * not. Everything else in the rail is a CATEGORY — what a message is — while these two are
 * a WORK QUEUE: what nobody has filed yet.
 *
 * Defined once, in `utils/` rather than `api/`, because the same set has to drive three
 * surfaces that must agree exactly: the drawer badge, the rail section, and the
 * `?label=needs-human` list filter. Two of those previously computed it independently and
 * disagreed the moment anyone opened a message.
 */

/** Slugs whose messages are unfiled and need a person. */
export const NEEDS_HUMAN_SLUGS = ['unmatched', 'needs-lot'] as const;

/**
 * Where a message LIVES — one value, never two (migration 261).
 *
 * Orthogonal to both the labels below and to `read_at`, and the three answer different
 * questions. This one is "which place is it in"; `HANDLED_SLUGS` is "did it produce
 * anything"; `read_at` is "has anyone looked". A message can be perfectly filed and still
 * open, or unfiled and archived.
 *
 * - `open`     — the working inbox. The ONLY state that counts toward a badge.
 * - `waiting`  — answered, now waiting on the homeowner. Out of the queue, NOT finished;
 *                an inbound reply flips it back to `open` (see api/email-inbound.ts).
 * - `spam`     — junk. Distinct from archived because archived mail is correspondence
 *                someone may go looking for, and junk is noise we keep only to prove we
 *                did not lose anything.
 * - `archived` — dealt with, filed away, findable.
 */
export const MESSAGE_STATUSES = ['open', 'waiting', 'spam', 'archived'] as const;

export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

export const isMessageStatus = (value: unknown): value is MessageStatus =>
	typeof value === 'string' && (MESSAGE_STATUSES as readonly string[]).includes(value);

/**
 * What each state is called in front of a user.
 *
 * Here rather than in the component because the rail, the list badge, the pane banner and
 * the history trail all name the same states, and four independent spellings of "Waiting
 * on customer" is how a UI starts reading like four different features.
 */
export const MESSAGE_STATUS_LABEL: Record<MessageStatus, string> = {
	open: 'Open',
	waiting: 'Waiting on customer',
	spam: 'Spam',
	archived: 'Archived',
};

/**
 * The states that are somewhere OTHER than the working inbox — each its own rail place.
 *
 * Derived from the list rather than written out, so adding a state to
 * {@link MESSAGE_STATUSES} cannot leave a place behind.
 */
export const AWAY_STATUSES = MESSAGE_STATUSES.filter((s) => s !== 'open');

/**
 * The inbox PLACES — each a route of its own.
 *
 * ── WHY ROUTES AND NOT QUERY PARAMS ──
 *
 * These used to be `?assignedTo=me`, `?label=needs-human`, `?status=archived` on `/inbox`,
 * and the chrome could not tell them apart reliably. A screen reads its own params with
 * `useLocalSearchParams` and gets the truth; a LAYOUT cannot — its local params are its own
 * segment's, not the active child's — so the rail and the drawer had to use
 * `useGlobalSearchParams`, which merges across the navigation state. A stale `assignedTo`
 * therefore survived a move to `label`, two rows matched at once, and both highlighted.
 *
 * A pathname has exactly one value, and `usePathname` is reliable everywhere. So two places
 * can no longer both be current — the ambiguity is gone by construction rather than by
 * matching rules getting cleverer, which is what three attempts at the param layer taught.
 *
 * FILTERS stay query params (`?label=`, `?associationId=`, `?q=`, `?showFiled=`): they
 * narrow whatever place you are in, several can apply at once, and nothing highlights
 * exclusively on them.
 */
export const INBOX_PLACES = ['all', 'mine', 'triage', 'waiting', 'spam', 'archived'] as const;

export type InboxPlace = (typeof INBOX_PLACES)[number];

export const isInboxPlace = (value: unknown): value is InboxPlace =>
	typeof value === 'string' && (INBOX_PLACES as readonly string[]).includes(value);

/**
 * `all` lives at `/inbox` itself; the rest are children of it.
 *
 * Returns a LITERAL union, not `string`, so Expo's typed routes still check these — a
 * helper that widened to `string` would quietly force `as Href` casts at every call site,
 * and those casts are exactly how a route rename shipped a dead link earlier in this work.
 */
export type InboxPlaceHref = '/inbox' | `/inbox/${Exclude<InboxPlace, 'all'>}`;

export const inboxPlaceHref = (place: InboxPlace): InboxPlaceHref =>
	(place === 'all' ? '/inbox' : `/inbox/${place}`);

/** The route path → which place it is. Unknown paths (a message id) are not a place. */
export const inboxPlaceForPath = (pathname: string): InboxPlace | null => {
	if (pathname === '/inbox' || pathname === '/inbox/') return 'all';
	const rest = pathname.startsWith('/inbox/') ? pathname.slice('/inbox/'.length).split('/')[0] : '';
	return isInboxPlace(rest) && rest !== 'all' ? rest : null;
};

/**
 * One name per place, used by the rail, the drawer and the screen banner alike.
 *
 * `triage` repeats the string rather than referencing `NEEDS_HUMAN_LABEL`, which is
 * declared below this — a `const` cannot be read before its initialiser runs, and a lazy
 * getter here would be cleverness in place of a seven-character duplication. The spec in
 * `tests/frontend/unit/inbox-places.spec.ts` asserts the two stay equal.
 */
export const INBOX_PLACE_LABEL: Record<InboxPlace, string> = {
	all: 'All mail',
	mine: 'Assigned to me',
	triage: 'Needs Triage',
	waiting: MESSAGE_STATUS_LABEL.waiting,
	spam: MESSAGE_STATUS_LABEL.spam,
	archived: MESSAGE_STATUS_LABEL.archived,
};

/** The three places that are just a message STATUS, so a screen can map one to a filter. */
export const PLACE_STATUS: Partial<Record<InboxPlace, Exclude<MessageStatus, 'open'>>> = {
	waiting: 'waiting',
	spam: 'spam',
	archived: 'archived',
};

/**
 * Reserved `?label=` value selecting the whole queue at once.
 *
 * Not a row in `labels` — a saved filter over the two slugs above. Kept distinct from real
 * slugs so creating a user label called "needs-human" could never hijack the queue.
 */
export const NEEDS_HUMAN_FILTER = 'needs-human';

/**
 * "Needs Triage", not "Needs Assignment".
 *
 * Deliberately avoids the word ASSIGNMENT, which now means something specific and different
 * in this rail: since the catch-all rule shipped, mail in this queue IS assigned to a
 * property manager — what it lacks is a matched owner. A row reading "Needs Assignment"
 * directly above one reading "Assigned to me" would use one word for two things.
 */
export const NEEDS_HUMAN_LABEL = 'Needs Triage';

/**
 * The SAME queue, named for what is actually missing — used only INSIDE an association.
 *
 * Portfolio-wide, the queue is a mix: `unmatched` mail has no association either, so the
 * only honest summary of both slugs is "somebody has to look at this". Nested under a named
 * association that ambiguity is gone — the community is known by construction, and the one
 * thing still missing is whose lot it is. "No Owner" says that; "Needs a Human" makes the
 * reader work out what kind of attention is wanted.
 *
 * Matches migration 223, which renamed the `needs-lot` label itself to "Needs Owner" for
 * the same reason.
 */
export const NO_OWNER_LABEL = 'No Owner';

/**
 * Reserved `?label=` value selecting mail that resolved to NO association (#411).
 *
 * Not a row in `labels` — the same trick as {@link NEEDS_HUMAN_FILTER}, for the same
 * reason. The rail's "No association" badge counts `association_id IS NULL`, and it
 * previously navigated to `?label=unmatched`, which is a DIFFERENT set: the backfill
 * strips `unmatched` the moment an account resolves, so a message can carry the label
 * without being unassociated and vice versa. The badge and the list it opened disagreed.
 */
export const NO_ASSOCIATION_FILTER = 'no-association';

/**
 * Counted by DISTINCT message, not by summing the two labels.
 *
 * Ingest applies exactly one of them, but a human can apply the other by hand; summing
 * would then report more work than there are messages, and the queue would never reach
 * zero no matter how much got filed.
 */
export const isNeedsHumanSlug = (slug: string): boolean =>
	(NEEDS_HUMAN_SLUGS as readonly string[]).includes(slug);

/**
 * Labels that mean a human has DELIBERATELY finished with a message.
 *
 * The escape hatch for "unfiled": plenty of mail correctly never becomes a work order or
 * a request, and without a way to say so it would flag forever and train staff to ignore
 * the flag. Marking it reviewed or dismissed is that way.
 */
export const HANDLED_SLUGS = ['reviewed', 'dismissed'] as const;

export const isHandledSlug = (slug: string): boolean =>
	(HANDLED_SLUGS as readonly string[]).includes(slug);

/**
 * "Nothing has been done with this yet" — the inverse of the row's `linked` marker.
 *
 * A message is FILED once it has produced something (a work order, an improvement
 * request) or once someone has explicitly reviewed or dismissed it. Anything else is
 * outstanding work, whatever its type.
 *
 * Deliberately about LINKS rather than labels: a label says what a message is about,
 * while a link is the only durable record that it turned into something. Note this is
 * a different question from `needs-human` — that one asks whether we know WHOSE mail it
 * is, this one asks whether anyone has acted on it. A message can be perfectly matched
 * to a lot and still be unfiled.
 */
export const isUnfiledMessage = (
	message: { links?: unknown[]; labels?: Array<{ slug: string }> },
): boolean => {
	if ((message.links?.length ?? 0) > 0) return false;
	return !(message.labels ?? []).some((label) => isHandledSlug(label.slug));
};
